import { ActionIcon, Group, Stack, Title, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { createFileRoute, Link } from "@tanstack/react-router";
import dayjs from "dayjs";

import { DataGridBase } from "@/components/@custom/DataGridBase";
import { Plate } from "@/components/@custom/Plate";
import { useSession } from "@/hooks/@custom/useSession";
import { useGetQueueByCompanyQuery } from "@/store/apis/nextcargo";
import { queueApi } from "@/store/apis/nextcargo/queue/queue.api";
import { cn } from "@/utils/cn";
import { ArrowLeftIcon } from "@phosphor-icons/react";

export const Route = createFileRoute("/app/painel/fila")({
  component: DriverQueuePanel
});

function DriverQueuePanel() {
  const { driver } = useSession();
  const driverId = String(driver?.id);
  const driverCompanyId = String(driver?.company.id);

  const { data: delay } = queueApi.endpoints.getQueueDelayStatusByCompany.useQueryState(
    { id: Number(driverCompanyId) },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        return {
          ...state,
          data: state.data?.delay?.reason
        };
      }
    }
  );

  const { data, isFetching } = useGetQueueByCompanyQuery({ id: Number(driverCompanyId)! }, { skip: !driverCompanyId });
  const rows =
    data?.original.map((item) => {
      const licensePlate = item?.scheduling ? item?.scheduling?.truck?.licensePlate : item?.truck?.licensePlate;
      const vehicleModel = item?.scheduling?.truck?.vehicleModel?.name;

      return {
        ...item,
        licensePlate,
        vehicleModel,
        position: `${item.position}°`,
        logged: item.driver.id === Number(driverId),
        freelancer: !!item.scheduling
      };
    }) || [];

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "position",
      headerName: "Fila",
      width: 60,
      sortable: false,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "licensePlate",
      headerName: "Placa",
      display: "flex",
      flex: 1,
      minWidth: 175,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => {
        return <Plate plate={value} color="text-black" />;
      }
    },
    {
      field: "createdAt",
      headerName: "Entrada na Fila",
      flex: 1,
      sortable: false,
      valueFormatter: (params) => dayjs(params).format("DD/MM, HH:mm ss[s]"),
      type: "dateTime",
      renderCell: ({ value }) => {
        const date = dayjs(value).format("DD/MM, HH:mm ss[s]");
        return (
          <Tooltip label="Entrada na fila" position="top-start">
            <p className="text-md font-semibold">{date}</p>
          </Tooltip>
        );
      }
    }
  ];

  console.log(delay);

  return (
    <Stack mt={delay ? "64px" : "md"} flex={1} className="max-w-2xl self-start!">
      <Group justify="space-between" align="center">
        <Title order={1}>Fila</Title>

        <ActionIcon component={Link} to="/app" className="shadow" size="xl" color="primary">
          <ArrowLeftIcon weight="bold" />
        </ActionIcon>
      </Group>

      <DataGridBase
        rows={rows}
        columns={columns}
        disableColumnMenu
        columnHeaderHeight={30}
        rowSelection={false}
        loading={isFetching}
        getRowClassName={(params) => {
          const isEven = params.indexRelativeToCurrentPage % 2 === 0;

          return cn(isEven ? "bg-white" : "bg-gray-50", isEven ? "hover:bg-gray-100" : "hover:bg-white");
        }}
        classes={{
          columnHeader: cn("bg-primary!"),
          columnHeaderTitle: cn("font-bold! text-white"),
          cell: cn("border border-r-0 border-l-0 border-dashed border-gray-600")
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 }
          },
          density: "comfortable"
        }}
      />
    </Stack>
  );
}
